import { format, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export default class DateHelper {
  //August 22, 2023 at 1:11 PM PDT
  public static longDateFormat(input: Date | string, forceOutputToLocalTimeZone = false): string {
    return this.formatDateString(input, `MMMM d, yyyy 'at' h:mm a zzz`, forceOutputToLocalTimeZone);
  }

  //8/24/2023 9:16pm
  public static mediumDateFormat(input: Date | string, forceOutputToLocalTimeZone = false): string {
    return this.formatDateString(input, 'M/d/yyyy h:mmaaa zzz', forceOutputToLocalTimeZone);
  }

  //8/24/2023
  public static shortDateFormat(input: Date | string, forceOutputToLocalTimeZone = false): string {
    return this.formatDateString(input, 'M/d/yyyy', forceOutputToLocalTimeZone);
  }

  // March 2025
  public static fullMonthAndYear(input: Date | string, forceOutputToLocalTimeZone = false): string {
    return this.formatDateString(input, 'MMMM yyyy', forceOutputToLocalTimeZone);
  }

  // March 12, 2025
  public static fullMonthDayYear(input: Date | string, forceOutputToLocalTimeZone = false): string {
    return this.formatDateString(input, 'MMMM dd, yyyy', forceOutputToLocalTimeZone);
  }

  public static formatDateStringAsUtc(date: Date | string, outputFormat: string): string {
    return formatInTimeZone(date, 'UTC', outputFormat);
  }

  public static formatDateString(date: Date | string, outputFormat: string, forceOutputToLocalTimeZone = false): string {
    if (date instanceof Date) {
      if (outputFormat.includes('z') || forceOutputToLocalTimeZone) {
        const targetTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return formatInTimeZone(date, targetTimeZone, outputFormat);
      } else {
        return format(date, outputFormat);
      }
    }

    try {
      if (typeof date === 'string') {
        // If the output string has a time zone displayed, we need to turn to date-fns-tz
        // so we can get the time zone abbreviation (ex. CDT, EST) to display
        if (outputFormat.includes('z') || forceOutputToLocalTimeZone) {
          const targetTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          return formatInTimeZone(date, targetTimeZone, outputFormat);
        } else {
          // Check if the date string is in ISO8601 format
          if (/Z$/i.test(date)) {
            return formatInTimeZone(date, 'UTC', outputFormat);
          } else {
            return format(new Date(date), outputFormat);
          }
        }
      }
    } catch {
      console.log(`${date} was not a valid date string.`);
    }

    return 'Invalid Date';
  }

  // Feb 28, 2025
  public static mmmDdYyyyFormat(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  public static dayOfTheWeek(input: Date): string {
    const date = new Date(input);
    return format(date, 'EEEE');
  }

  //Same as medium, but replaces date with "Today" or "Tomorrow"
  public static casualDateFormat(input: Date, formatInUTC = false): string {
    const date = new Date(input);

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      const formatString = 'h:mm a';
      if (formatInUTC) {
        return `Today, ${formatInTimeZone(date, 'UTC', formatString)}`;
      } else {
        return `Today, ${format(date, formatString)}`;
      }
    }

    if (date.toDateString() === tomorrow.toDateString()) {
      const formatString = 'h:mm a';
      if (formatInUTC) {
        return `Tomorrow, ${formatInTimeZone(date, 'UTC', formatString)}`;
      } else {
        return `Tomorrow, ${format(date, formatString)}`;
      }
    }

    const formatString = 'M/d/yyyy h:mm a';
    if (formatInUTC) {
      return formatInTimeZone(date, 'UTC', formatString);
    } else {
      return format(date, formatString);
    }
  }

  public static getFullYearsBetweenDates(startDate: Date, endDate: Date): number {
    // Calculate the difference in years
    const yearDifference = endDate.getFullYear() - startDate.getFullYear();

    // Check if the end date has not occurred yet in the current year
    if (
      endDate.getMonth() < startDate.getMonth() ||
      (endDate.getMonth() === startDate.getMonth() && endDate.getDate() < startDate.getDate())
    ) {
      return yearDifference - 1;
    } else {
      return yearDifference;
    }
  }

  public static isValidDate(input: string) {
    // Attempt to create a Date object from the provided string
    const dateObject = new Date(input);

    // Check if the created object is a valid date
    return !isNaN(dateObject.getTime());
  }

  public static printYearRange(startDateText: Date, endDateText: Date) {
    const startDate = new Date(startDateText);
    const endDate = new Date(endDateText);
    const minDate = new Date(0);
    const today = new Date();

    if (startDate > minDate && endDate > minDate && endDate < today) {
      //Return full year start and end
      return startDate.getUTCFullYear() + ' - ' + endDate.getUTCFullYear();
    }
    if (startOfDay(startDate) > startOfDay(minDate)) {
      return startDate.getUTCFullYear() + ' - Present';
    }

    return '';
  }

  public static printMonthYearLong(input: Date) {
    const dateObject = new Date(input);
    const formattedDate = dateObject.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return formattedDate;
  }
}
